import { isSameDay, startOfDay } from "date-fns";
import type { Chore, Member } from "@/lib/schemas";

/**
 * Wartości pola `chore.assignedTo`:
 *  - `CHORE_ASSIGN_ANY`          ("any")          — każdy z HH może wziąć
 *  - `CHORE_ASSIGN_ALL_CHILDREN` ("all-children") — predefiniowana grupa
 *    "wszystkie dzieci HH". Każde dziecko widzi i zgłasza osobno
 *    (completion per dziecko, jak przy "any").
 *  - inne stringi — uid konkretnego usera.
 */
export const CHORE_ASSIGN_ANY = "any";
export const CHORE_ASSIGN_ALL_CHILDREN = "all-children";

/**
 * Czy chore jest widoczny dla user'a (dziecka) na jego liście zadań.
 * Wspólny helper żeby `assignedTo === "any" || assignedTo === uid` —
 * powtarzane w 10+ miejscach — miało jeden punkt prawdy.
 */
export function isChoreForUser(assignedTo: string, uid: string): boolean {
  return (
    assignedTo === CHORE_ASSIGN_ANY ||
    assignedTo === CHORE_ASSIGN_ALL_CHILDREN ||
    assignedTo === uid
  );
}

/**
 * Etykieta UI dla pola assignedTo. Dla predefiniowanych grup zwraca
 * polską nazwę ("Każdy"/"Dzieci"), dla uid — displayName członka.
 */
export function assignedToLabel(
  assignedTo: string,
  members: Pick<Member, "userId" | "displayName">[],
): string {
  if (assignedTo === CHORE_ASSIGN_ANY) return "Każdy";
  if (assignedTo === CHORE_ASSIGN_ALL_CHILDREN) return "Dzieci";
  return members.find((m) => m.userId === assignedTo)?.displayName ?? "—";
}

/**
 * Czy zadanie powinno dziś być wykonane wg jego harmonogramu.
 *
 * - daily: zawsze tak
 * - weekly: jeśli today.getDay() pasuje do daysOfWeek (0=Nd … 6=Sb)
 * - once: jeśli today === dueDate (porównanie kalendarzowe)
 *
 * Dzieci nie powinny móc kliknąć „Zrobione" w innym dniu — bo wtedy
 * zliczyłoby się jako wykonanie na ten dzień, czego nie chcemy.
 * Wyjątek: chore daily zawsze aktywne.
 */
export function isChoreActiveToday(
  chore: Pick<Chore, "schedule">,
  now: Date = new Date(),
): boolean {
  if (chore.schedule.type === "daily") return true;
  if (chore.schedule.type === "weekly") {
    return chore.schedule.daysOfWeek.includes(now.getDay());
  }
  if (chore.schedule.type === "once") {
    return isSameDay(startOfDay(now), startOfDay(chore.schedule.dueDate.toDate()));
  }
  return false;
}
